import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

export interface WclProgressSnapshot {
    done: number;
    planned: number;
    cached: number;
    inFlight: number;
    workCurrent: number;
    workTotal: number;
    workLabel: string;
    throttling: boolean;
}

function newProgressId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `wcl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useWclProgress() {
    const [progressId, setProgressId] = useState<string | null>(null);
    const [snapshot, setSnapshot] = useState<WclProgressSnapshot | null>(null);
    const progressIdRef = useRef<string | null>(null);

    const begin = useCallback(() => {
        const id = newProgressId();
        progressIdRef.current = id;
        setProgressId(id);
        setSnapshot(null);
        return id;
    }, []);

    const end = useCallback(() => {
        progressIdRef.current = null;
        setProgressId(null);
        setSnapshot(null);
    }, []);

    useEffect(() => {
        if (!progressId) {
            return;
        }
        let cancelled = false;
        const poll = async () => {
            try {
                const response = await axios.get<WclProgressSnapshot>(`/api/attendance/wcl-progress/${progressId}`, {
                    timeout: 5000,
                });
                if (!cancelled && progressIdRef.current === progressId) {
                    setSnapshot(response.data);
                }
            } catch {
                // 404 before the job registers is fine.
            }
        };
        void poll();
        const timer = window.setInterval(() => {
            void poll();
        }, 400);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [progressId]);

    return { begin, end, snapshot, progressId };
}
